/**
 * Clients list — DataTable enterprise + Sheet form.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { bulkApi } from "@/lib/api-bulk";
import { downloadCsv } from "@/lib/api-system";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { clientsApi, type Client } from "@/lib/api-cadastros";
import { formatCNPJ, formatPhone } from "@/lib/format";

export function ClientsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Client>>({ active: true });
  const [bulkConfirm, setBulkConfirm] = useState<{ ids: string[] } | null>(null);

  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => bulkApi.deleteClients(ids),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(`${result.deleted} cliente(s) excluído(s)`);
      setBulkConfirm(null);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const list = useQuery({
    queryKey: ["clients"],
    queryFn: () => clientsApi.list({ size: 200 }),
  });

  const create = useMutation({
    mutationFn: (body: Partial<Client>) => clientsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente criado");
      setOpen(false);
      setForm({ active: true });
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const columns: ColumnDef<Client>[] = [
    {
      id: "trade_name",
      accessorKey: "trade_name",
      header: "Nome fantasia",
      cell: ({ row }) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar size="md">
            <AvatarFallback name={row.original.trade_name} />
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium truncate">{row.original.trade_name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {row.original.corporate_name}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "cnpj",
      accessorKey: "cnpj",
      header: "CNPJ",
      cell: ({ row }) => <span className="font-mono text-sm">{formatCNPJ(row.original.cnpj)}</span>,
    },
    {
      id: "email",
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => row.original.email ?? <span className="text-muted-foreground">—</span>,
    },
    {
      id: "phone",
      accessorKey: "phone",
      header: "Telefone",
      cell: ({ row }) => formatPhone(row.original.phone),
    },
    {
      id: "active",
      accessorKey: "active",
      header: "Status",
      cell: ({ row }) =>
        row.original.active ? <Badge variant="success" dot>Ativo</Badge> : <Badge variant="muted" dot>Inativo</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        title="Clientes"
        description="Empresas contratantes da plataforma."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => downloadCsv("clients")}>
              <Download className="h-4 w-4" /> Exportar
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> Novo cliente
            </Button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar por nome, CNPJ, email..."
        emptyTitle="Nenhum cliente cadastrado"
        emptyDescription="Crie o primeiro cliente pra começar a operar."
        emptyAction={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Novo cliente
          </Button>
        }
        onRowClick={(c) => navigate(`/clients/${c.id}`)}
        enableSelection
        getRowId={(c) => c.id}
        bulkActions={(rows) => (
          <Button
            size="sm"
            variant="outline"
            className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
            onClick={() => setBulkConfirm({ ids: rows.map((r) => r.id) })}
          >
            <Trash2 className="h-3.5 w-3.5" /> Excluir selecionados
          </Button>
        )}
        columnLabels={{
          trade_name: "Nome fantasia",
          cnpj: "CNPJ",
          email: "Email",
          phone: "Telefone",
          active: "Status",
        }}
      />

      <ConfirmDialog
        open={!!bulkConfirm}
        onOpenChange={(v) => !v && setBulkConfirm(null)}
        variant="danger"
        title={`Excluir ${bulkConfirm?.ids.length ?? 0} cliente(s)?`}
        description="Esta ação não pode ser desfeita."
        confirmLabel="Sim, excluir"
        loading={bulkDelete.isPending}
        onConfirm={() => bulkConfirm && bulkDelete.mutate(bulkConfirm.ids)}
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent size="lg">
          <SheetHeader>
            <SheetTitle>Novo cliente</SheetTitle>
            <SheetDescription>Empresa contratante da Eunerd.</SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <Field label="Nome fantasia" required>
              <Input
                value={form.trade_name ?? ""}
                onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
              />
            </Field>
            <Field label="Razão social" required>
              <Input
                value={form.corporate_name ?? ""}
                onChange={(e) => setForm({ ...form, corporate_name: e.target.value })}
              />
            </Field>
            <Field label="CNPJ (14 dígitos, sem máscara)" required>
              <Input
                value={form.cnpj ?? ""}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value.replace(/\D/g, "") })}
                maxLength={14}
                className="font-mono"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <Input
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <Field label="Telefone">
                <Input
                  value={form.phone ?? ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Website">
              <Input
                value={form.website ?? ""}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </Field>
            <Field label="Observações">
              <Textarea
                rows={3}
                value={form.notes ?? ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline">Cancelar</Button>
            </SheetClose>
            <Button
              onClick={() => create.mutate(form)}
              disabled={create.isPending || !form.trade_name || !form.corporate_name || !form.cnpj || form.cnpj.length !== 14}
            >
              {create.isPending ? "Criando..." : "Criar cliente"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      {children}
    </div>
  );
}
